// ===========================================
// SMS/WhatsApp Template API Client
// ===========================================
// API client for managing school SMS/WhatsApp message templates

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface SmsCharacterInfo {
  length: number;
  smsSegments: number;
  isUnicode: boolean;
  charsPerSegment: number;
  withinWhatsAppLimit: boolean;
}

export interface SmsTemplate {
  key: string;
  name: string;
  category: string;
  description: string;
  enabled: boolean;
  isCustomized: boolean;
  characterInfo: SmsCharacterInfo;
}

export interface SmsMergeField {
  key: string;
  placeholder: string;
  label: string;
  description: string;
  example: string;
  required: boolean;
}

export interface SmsTemplateMetadata {
  key: string;
  name: string;
  category: string;
  description: string;
  defaultBody: string;
  maxLength: number;
  mergeFields: SmsMergeField[];
}

export interface CustomSmsBody {
  body: string;
  createdAt: string;
  updatedAt: string;
}

export interface SmsTemplateDetail {
  key: string;
  metadata: SmsTemplateMetadata;
  customBody: CustomSmsBody | null;
  enabled: boolean;
  isCustomized: boolean;
}

export interface SmsPreviewResult {
  rendered: string;
  characterInfo: SmsCharacterInfo;
  isCustom: boolean;
}

export interface SmsTestResult {
  message: string;
  results: {
    sms?: string;
    whatsapp?: string;
  };
}

// ===========================================
// API FUNCTIONS
// ===========================================

const BASE_PATH = '/admin/settings/sms-templates';

/**
 * Get all SMS/WhatsApp templates with customization status
 */
export async function getSmsTemplates(): Promise<SmsTemplate[]> {
  return apiClient.get<SmsTemplate[]>(BASE_PATH);
}

/**
 * Get detailed info for a single SMS template
 */
export async function getSmsTemplateDetail(templateKey: string): Promise<SmsTemplateDetail> {
  return apiClient.get<SmsTemplateDetail>(`${BASE_PATH}/${templateKey}`);
}

/**
 * Save custom message body for a template
 */
export async function setCustomSmsBody(
  templateKey: string,
  body: string
): Promise<{ message: string; body: CustomSmsBody; characterInfo: SmsCharacterInfo }> {
  return apiClient.put<{ message: string; body: CustomSmsBody; characterInfo: SmsCharacterInfo }>(
    `${BASE_PATH}/${templateKey}/body`,
    { body }
  );
}

/**
 * Reset message body to default
 */
export async function resetCustomSmsBody(
  templateKey: string
): Promise<{ message: string; defaultBody: string; characterInfo: SmsCharacterInfo }> {
  return apiClient.delete<{ message: string; defaultBody: string; characterInfo: SmsCharacterInfo }>(
    `${BASE_PATH}/${templateKey}/body`
  );
}

/**
 * Toggle SMS template enabled/disabled
 */
export async function toggleSmsTemplate(
  templateKey: string,
  enabled: boolean
): Promise<{ message: string }> {
  return apiClient.put<{ message: string }>(
    `${BASE_PATH}/${templateKey}/toggle`,
    { enabled }
  );
}

/**
 * Preview template with sample data
 */
export async function previewSmsTemplate(templateKey: string): Promise<SmsPreviewResult> {
  return apiClient.post<SmsPreviewResult>(`${BASE_PATH}/${templateKey}/preview`);
}

/**
 * Send test SMS/WhatsApp message
 */
export async function sendTestSms(
  templateKey: string,
  phoneNumber: string
): Promise<SmsTestResult> {
  return apiClient.post<SmsTestResult>(`${BASE_PATH}/${templateKey}/test`, { phoneNumber });
}
